import { ContractSigningCopyKey, contractSigningTranslations } from '@/helpers/contractSigningTranslations';

import { Locale } from 'types';

/**
 * Before the contract language is set, every line shows in both languages. After, English contracts show
 * English, and Spanish contracts always carry the English underneath.
 */
export const displayLocalesFor = (contractLanguage: Locale | null): Locale[] => {
  if (contractLanguage === Locale.en) return [Locale.en];
  if (contractLanguage === Locale.es) return [Locale.es, Locale.en];
  return [Locale.en, Locale.es];
};

export type CopyVars = Record<string, string | number>;

export const copyText = (locale: Locale, key: ContractSigningCopyKey, vars?: CopyVars): string =>
  Object.entries(vars ?? {}).reduce(
    (text, [name, value]) => text.replaceAll(`{${name}}`, String(value)),
    contractSigningTranslations[locale][key],
  );

const STYLES = {
  heading: { primary: 'heading-500 text-text-primary', secondary: 'body-300 text-text-secondary' },
  body: { primary: 'body-300 text-text-primary', secondary: 'body-200 text-text-secondary' },
  error: { primary: 'body-300 text-text-error', secondary: 'body-200 text-text-error' },
} as const;

interface CopyProps {
  copyKey: ContractSigningCopyKey;
  locales: Locale[];
  vars?: CopyVars;
  variant?: keyof typeof STYLES;
}

export const Copy = ({ copyKey, locales, vars, variant = 'body' }: CopyProps) => {
  const [primary, ...secondary] = locales;
  const Tag = variant === 'heading' ? 'h1' : 'p';
  return (
    <div className="gap-1 flex flex-col">
      <Tag className={STYLES[variant].primary} lang={primary === Locale.es ? 'es' : 'en'}>
        {copyText(primary, copyKey, vars)}
      </Tag>
      {secondary.map((locale) => (
        <p key={locale} className={STYLES[variant].secondary} lang={locale === Locale.es ? 'es' : 'en'}>
          {copyText(locale, copyKey, vars)}
        </p>
      ))}
    </div>
  );
};

/** Labels for form controls, which only take a string: languages joined on one line. */
export const inlineCopy = (locales: Locale[], key: ContractSigningCopyKey, vars?: CopyVars): string =>
  [...new Set(locales.map((locale) => copyText(locale, key, vars)))].join(' / ');

export const SupportContact = ({ locales }: { locales: Locale[] }) => (
  <p className="body-200 text-text-secondary">
    {inlineCopy(locales, 'contactLabel')}{' '}
    <a className="text-text-link underline" href={`tel:${copyText(Locale.en, 'contactPhone').replace(/\D/g, '')}`}>
      {copyText(Locale.en, 'contactPhone')}
    </a>
  </p>
);
